import React from "react";
import {Box, ButtonBase, Card, LinearProgress, Stack, TextField, Tooltip, Typography, useMediaQuery} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {EvaluationPoint, EvaluationResult} from "arteco-api-client-ts";
import {panelSx} from "../../../../components/kit";

/**
 * Piezas comunes de las autoevaluaciones: la escala de respuesta, la puntuación y el formulario, que
 * usan igual la ficha de RR. HH. y la pantalla del empleado.
 *
 * Cada respuesta tiene un nivel de 0 a 40 y la puntuación de una autoevaluación es su media; en
 * pantalla se enseña sobre 10, que se lee mejor.
 */

/** Colores de la escala, de «Totalmente en desacuerdo» a «Totalmente de acuerdo». */
const SCALE_COLORS = ["#d32f2f", "#f57c00", "#546e7a", "#66bb6a", "#2e7d32"];

export const score10 = (rating?: number | null) => rating === undefined || rating === null ? undefined : rating / 4;

export const formatScore = (rating?: number | null) => {
	const s = score10(rating);
	return s === undefined ? "—" : s.toLocaleString("es-ES", {minimumFractionDigits: 1, maximumFractionDigits: 1});
};

export const scoreColor = (rating?: number | null) => {
	const s = score10(rating);
	if (s === undefined) {
		return "#9e9e9e";
	}
	return s >= 7.5 ? SCALE_COLORS[4] : s >= 6 ? SCALE_COLORS[3] : s >= 4.5 ? "#f9a825" : s >= 3 ? SCALE_COLORS[1] : SCALE_COLORS[0];
};

/** Puntuación en una píldora con su color: «6,4». */
export const ScoreBadge = ({rating, size = "small"}: { rating?: number | null, size?: "small" | "large" }) => {
	const c = scoreColor(rating);
	const large = size === "large";
	return (
		<Box component="span" sx={{
			display: "inline-flex", alignItems: "baseline", gap: 0.25, px: large ? 1.5 : 1, py: large ? 0.5 : 0.25, borderRadius: 99,
			bgcolor: alpha(c, 0.12), color: c, fontWeight: 700, fontVariantNumeric: "tabular-nums",
			fontSize: large ? "2rem" : "0.85rem", lineHeight: 1.2,
		}}>
			{formatScore(rating)}
			<Box component="span" sx={{fontSize: "0.55em", fontWeight: 600, opacity: 0.8}}>/10</Box>
		</Box>
	);
};

/** Barra fina con la puntuación sobre 10. */
export const ScoreBar = ({rating}: { rating?: number | null }) => (
	<LinearProgress variant="determinate" value={Math.max(0, Math.min(100, (rating || 0) * 100 / 40))} sx={{
		height: 6, borderRadius: 3, bgcolor: "grey.100", "& .MuiLinearProgress-bar": {borderRadius: 3, bgcolor: scoreColor(rating)},
	}}/>
);

export type Answers = { [pointId: string]: EvaluationResult | undefined };

interface LikertProps {
	index: number;
	point: EvaluationPoint;
	results: EvaluationResult[];
	value?: EvaluationResult;
	readOnly?: boolean;
	onChange: (r: EvaluationResult) => void;
}

/** Una pregunta con sus cinco respuestas en fila; en móvil, números y la etiqueta debajo. */
const Likert = ({index, point, results, value, readOnly, onChange}: LikertProps) => {
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("sm"));
	return (
		<Box sx={{py: 2, borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": {borderBottom: 0}}}>
			<Stack direction="row" spacing={1.5} alignItems="baseline">
				<Typography variant="caption" color="text.secondary" sx={{fontVariantNumeric: "tabular-nums", minWidth: 20}}>{index}.</Typography>
				<Typography variant="body2" fontWeight={600}>{point.label}</Typography>
			</Stack>
			<Box role="radiogroup" aria-label={point.label}
				 sx={{display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 0.75, mt: 1.25, pl: {sm: 4.25}}}>
				{results.map((r, i) => {
					const c = SCALE_COLORS[Math.min(i, SCALE_COLORS.length - 1)];
					const selected = value?.id === r.id;
					return (
						<Tooltip key={r.id} title={compact ? r.label : ""}>
							<ButtonBase role="radio" aria-checked={selected} aria-label={r.label} disabled={readOnly}
										onClick={() => onChange(r)}
										sx={{
											py: compact ? 1 : 1.1, px: 0.5, borderRadius: 2, fontSize: 12, fontWeight: 600, lineHeight: 1.2,
											border: "1.5px solid", borderColor: selected ? c : "divider",
											bgcolor: selected ? alpha(c, 0.12) : "background.paper",
											color: selected ? c : "text.secondary",
											transition: "all .12s", "&:hover": {borderColor: c},
											"&.Mui-disabled": {color: selected ? c : "text.disabled", opacity: selected ? 1 : 0.6},
										}}>
								{compact ? i + 1 : r.label}
							</ButtonBase>
						</Tooltip>
					);
				})}
			</Box>
			{compact && value && (
				<Typography variant="caption" sx={{display: "block", mt: 0.75, color: SCALE_COLORS[results.findIndex(r => r.id === value.id)]}}>
					{value.label}
				</Typography>
			)}
		</Box>
	);
};

export interface EvaluationComments {
	personalComments?: string;
	teamComments?: string;
	projectComments?: string;
	companyComments?: string;
}

const COMMENT_FIELDS: { field: keyof EvaluationComments, label: string, placeholder: string }[] = [
	{field: "personalComments", label: "Personal", placeholder: "Cómo estás tú: carga, motivación, lo que te preocupa…"},
	{field: "teamComments", label: "Equipo", placeholder: "Cómo funciona el equipo, la relación con los compañeros…"},
	{field: "projectComments", label: "Proyecto", placeholder: "El proyecto o el cliente en el que estás…"},
	{field: "companyComments", label: "Empresa", placeholder: "Lo que Arteco podría hacer mejor…"},
];

interface EvaluationEditorProps {
	points: EvaluationPoint[];
	results: EvaluationResult[];
	answers: Answers;
	comments: EvaluationComments;
	readOnly?: boolean;
	commentsHint?: string;
	onAnswer: (pointId: number, r: EvaluationResult) => void;
	onComment: (field: keyof EvaluationComments, value: string) => void;
}

/** Las preguntas con su escala y el progreso, y los cuatro comentarios. */
export const EvaluationEditor = ({points, results, answers, comments, readOnly, commentsHint, onAnswer, onComment}: EvaluationEditorProps) => {
	const answered = points.filter(p => answers[String(p.id)]).length;
	const sorted = [...results].sort((a, b) => a.level - b.level);
	return (
		<Stack spacing={3}>
			<Card elevation={0} sx={panelSx}>
				<Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between" alignItems={{sm: "center"}} spacing={1}>
					<Box>
						<Typography variant="subtitle1" fontWeight={700}>Preguntas</Typography>
						<Typography variant="body2" color="text.secondary">
							{readOnly ? "Respuestas de la autoevaluación." : "Elige la respuesta que mejor describa cómo lo vives ahora."}
						</Typography>
					</Box>
					<Box sx={{minWidth: 180}}>
						<Typography variant="caption" color="text.secondary">{answered} de {points.length} respondidas</Typography>
						<LinearProgress variant="determinate" value={points.length ? answered * 100 / points.length : 0}
										color={answered === points.length ? "success" : "primary"}
										sx={{height: 6, borderRadius: 3, mt: 0.5, "& .MuiLinearProgress-bar": {borderRadius: 3}}}/>
					</Box>
				</Stack>
				<Box sx={{mt: 1}}>
					{points.map((p, i) => (
						<Likert key={p.id} index={i + 1} point={p} results={sorted} value={answers[String(p.id)]} readOnly={readOnly}
								onChange={r => onAnswer(p.id as number, r)}/>
					))}
				</Box>
			</Card>

			<Card elevation={0} sx={panelSx}>
				<Typography variant="subtitle1" fontWeight={700}>Comentarios</Typography>
				<Typography variant="body2" color="text.secondary">{commentsHint || "Opcionales."}</Typography>
				<Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, gap: 2, mt: 2}}>
					{COMMENT_FIELDS.map(f => (
						<TextField key={f.field} label={f.label} multiline minRows={3} value={comments[f.field] || ""}
								   placeholder={f.placeholder} InputLabelProps={{shrink: true}} InputProps={{readOnly}}
								   onChange={e => onComment(f.field, e.target.value)}/>
					))}
				</Box>
			</Card>
		</Stack>
	);
};

/** Primera línea con contenido de los comentarios, para los listados. */
export const firstComment = (c: EvaluationComments) =>
	[c.personalComments, c.teamComments, c.projectComments, c.companyComments].map(s => (s || "").trim()).find(Boolean) || "";
